

import { useState } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { RarityGlow } from './RarityGlow';
import { asset } from '@/lib/asset';

type Rarity = 'PROSPECT' | 'ROOKIE' | 'VETERAN' | 'ALL_STAR' | 'GOAT';

const RARITY_BORDER = {
  PROSPECT: 'border-[#9BA3AF]/50',
  ROOKIE: 'border-[#2563EB]/50',
  VETERAN: 'border-[#DC2626]/50',
  ALL_STAR: 'border-[#059669]/50',
  GOAT: 'border-gold/50',
};

const RARITY_LABEL_COLOR = {
  PROSPECT: 'text-[#9BA3AF]',
  ROOKIE: 'text-[#2563EB]',
  VETERAN: 'text-[#DC2626]',
  ALL_STAR: 'text-[#059669]',
  GOAT: 'text-gold',
};

interface CardRevealProps {
  playerName: string;
  team: string;
  position: string;
  rarity: Rarity;
  imageUrl: string | null;
  index: number;
  isRevealed: boolean;
  onReveal: () => void;
}

export function CardReveal({
  playerName,
  team,
  position,
  rarity,
  imageUrl,
  index,
  isRevealed,
  onReveal,
}: CardRevealProps) {
  const [hasFlipped, setHasFlipped] = useState(false);

  const handleClick = () => {
    if (!isRevealed && !hasFlipped) {
      setHasFlipped(true);
      onReveal();
    }
  };

  return (
    <motion.div
      className="perspective-1000"
      initial={{ opacity: 0, scale: 0.8, y: 50 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
    >
      <motion.div
        className="relative w-48 h-72 cursor-pointer preserve-3d"
        animate={{ rotateY: hasFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        onClick={handleClick}
      >
        {/* Card Back — Parchment with logo */}
        <div className="card-face absolute inset-0">
          <div className="w-full h-full rounded-xl bg-parchment border-2 border-parchment-border shadow-xl overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(184,160,76,0.08),transparent_70%)]" />
            <div className="h-full flex flex-col items-center justify-center">
              <Image
                src={asset('logo.webp')}
                alt="FantasyPRIME"
                width={64}
                height={64}
                className="mb-4 opacity-60"
              />
              <p className="text-tertiary text-sm font-serif italic">Tap to reveal</p>
            </div>
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 opacity-[0.03]">
              <div className="h-full w-full" style={{
                backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.1) 10px, rgba(0,0,0,0.1) 20px)`,
              }} />
            </div>
          </div>
        </div>

        {/* Card Front — Parchment with player */}
        <div className="card-face card-face-back absolute inset-0">
          <RarityGlow rarity={rarity} className="w-full h-full">
            <div
              className={cn(
                'w-full h-full rounded-xl overflow-hidden border-2 bg-parchment',
                RARITY_BORDER[rarity]
              )}
            >
              {/* Rarity Label */}
              <div className="pt-2 text-center">
                <span className={cn(
                  'text-[9px] tracking-[0.25em] uppercase font-bold',
                  RARITY_LABEL_COLOR[rarity]
                )}>
                  {rarity}
                </span>
              </div>

              {/* Player Image */}
              <div className="h-40 mt-1 flex items-center justify-center px-4">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={playerName}
                    className="h-full w-auto object-contain"
                    style={{ filter: 'sepia(0.3) contrast(1.1)' }}
                  />
                ) : (
                  <div className="h-24 w-24 rounded-full bg-parchment-dark border border-parchment-border flex items-center justify-center">
                    <span className="text-3xl font-serif font-bold text-surface/40">
                      {playerName.charAt(0)}
                    </span>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="text-center text-parchment-border tracking-[0.5em] text-[8px]">
                · · ·
              </div>

              {/* Player Info */}
              <div className="px-4 pb-3 text-center">
                <p className="font-serif font-bold text-surface text-base truncate">
                  {playerName.toUpperCase()}
                </p>
                <div className="flex items-center justify-center gap-2 mt-1">
                  <span className="text-[#7D7568] text-xs">{position}</span>
                  <span className="text-parchment-border text-xs">·</span>
                  <span className="text-[#7D7568] text-xs">{team}</span>
                </div>
              </div>
            </div>
          </RarityGlow>
        </div>
      </motion.div>
    </motion.div>
  );
}
